import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useUseCaseConfig } from "./useUseCaseConfig";
import FieldBuilder from "./FieldBuilder";
import { useSourceColumns } from "./useSourceColumns";
import ChecklistBuilder from "./ChecklistBuilder";
import DetailBuilder from "./DetailBuilder";
import DetailPreview from "./DetailPreview";
import FormPreview from "./FormPreview";
import VersionsTab from "./VersionsTab";
import ExcludedSitesInput from "./ExcludedSitesInput";
import {
  UseCaseConfig,
  ReviewSampling,
  FormConfigField,
  ChecklistItem,
  AdditionalViewField,
  DEFAULT_CHECKLIST_OPTIONS,
  USE_CASE_CATEGORY,
  UseCaseCategoryValue,
  DATA_SOURCE_TYPE,
  DATA_SOURCE_LABELS,
  DataSourceTypeValue,
  INGESTION_SOURCE_TYPE,
  INGESTION_SOURCE_LABELS,
  IngestionSourceTypeValue,
  OPTION_FIELD_TYPES,
  FIELD_TYPE_LABELS,
  makeUseCaseConfig,
  PermissionsMember,
  ROLE,
  UseCaseAllocation,
  IngestionSampling,
} from "../shared/types";
import * as S from "../shared/styles";
import { useAuditAccess } from "../shared/useAuditAccess";
import auditModuleService, {
  SamplingRunConfig,
  SamplingRunResult,
} from "src/services/auditModuleService";
import Loader from "@amzn/meridian/loader";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";

// ----------------------------  Tabs  ----------------------------

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "fields", label: "Fields" },
  { key: "checklist", label: "Checklist" },
  { key: "details", label: "Override" },
  { key: "ingestion", label: "Ingestion Sampling" },
  { key: "sampling", label: "Review Sampling" },
  { key: "auditors", label: "Auditors" },
  { key: "versions", label: "Versions" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

// ----------------------------  Validation  ----------------------------

interface ConfigErrors {
  fieldErrors: string[];
  checklistErrors: string[];
}

function validateConfig(fields: FormConfigField[], checklist: ChecklistItem[]): ConfigErrors {
  const fieldErrors: string[] = [];
  const checklistErrors: string[] = [];
  if (fields.length === 0) fieldErrors.push("Add at least one field.");
  fields.forEach((f, i) => {
    const tag = "Field " + (i + 1);
    if (!f.label?.trim()) fieldErrors.push(tag + ": label is required.");
    if (OPTION_FIELD_TYPES.indexOf(f.type) !== -1 && f.options.filter(Boolean).length === 0)
      fieldErrors.push(tag + " (" + FIELD_TYPE_LABELS[f.type] + "): needs at least one option.");
  });
  const seenLabels = new Set<string>();
  checklist.forEach((c, i) => {
    const tag = "Checklist item " + (i + 1);
    const label = c.label?.trim() ?? "";
    if (!label) checklistErrors.push(tag + ": label is required.");
    else if (seenLabels.has(label))
      checklistErrors.push(tag + ': duplicate question "' + label + '".');
    seenLabels.add(label);
    const opts = (c.options ?? DEFAULT_CHECKLIST_OPTIONS).map((o) => o.trim());
    if (opts.length === 0) checklistErrors.push(tag + ": needs at least one button.");
    if (opts.some((o) => !o)) checklistErrors.push(tag + ": button labels cannot be blank.");
    if (new Set(opts).size !== opts.length)
      checklistErrors.push(tag + ": button labels must be unique.");
  });
  return { fieldErrors, checklistErrors };
}

function validateDetails(details: AdditionalViewField[], enableFlags: string[]): string[] {
  const errors: string[] = [];
  if (details.length > 0 && enableFlags.length === 0)
    errors.push("Pick at least one flag column; the additional view shows only where one is true.");
  details.forEach((d, i) => {
    const tag = "Row " + (i + 1);
    if (!d.label.trim()) errors.push(tag + ": label is required.");
    if (!d.source_columns.some((c) => c.trim())) errors.push(tag + ": pick a source column.");
  });
  return errors;
}

// ----------------------------  Main Component  ----------------------------

const UseCaseEditor: React.FC = () => {
  const { useCaseId } = useParams<{ useCaseId?: string }>();
  const navigate = useNavigate();
  const {
    configs,
    isLoading,
    isError,
    fetchError,
    authError,
    configById,
    saveUseCase,
    updateUseCase,
    isSaving,
    saveSuccess,
    saveError,
    resetSave,
    retryFetch,
  } = useUseCaseConfig();

  const editing = Boolean(useCaseId);
  const { canManageUseCase } = useAuditAccess();
  const sourceColumns = useSourceColumns();

  // Initialise state once configs are loaded (for edit mode).
  const [initialised, setInitialised] = useState(!editing);
  const [tab, setTab] = useState<TabKey>("overview");
  const [uc, setUc] = useState<UseCaseConfig>(() => makeUseCaseConfig());
  const [showSaveError, setShowSaveError] = useState(false);

  // When editing, seed state from the store once loading finishes.
  useEffect(() => {
    if (!editing || initialised || isLoading) return;
    const existing = configById(useCaseId!);
    if (existing) {
      setUc({
        ...existing,
        fields: existing.fields.map((f) => ({ ...f, options: [...f.options] })),
        checklist_items: existing.checklist_items.map((c) => ({
          ...c,
          options: [...(c.options ?? DEFAULT_CHECKLIST_OPTIONS)],
        })),
        additional_view_fields: (existing.additional_view_fields ?? []).map((d) => ({
          ...d,
          source_columns: [...d.source_columns],
        })),
      });
    }
    // Mark initialised even when not found so the not-found branch can render.
    setInitialised(true);
  }, [editing, initialised, isLoading, useCaseId, configById]);

  const setU = (patch: Partial<UseCaseConfig>) => {
    setUc((prev) => ({ ...prev, ...patch }));
    // Dismiss save error when the user modifies the form.
    if (showSaveError) setShowSaveError(false);
  };

  const ingestion: IngestionSampling = uc.ingestion_sampling ?? {};
  const setIngestion = (patch: Partial<IngestionSampling>) =>
    setU({ ingestion_sampling: { ...ingestion, ...patch } });

  // A rollback created a new active version server-side: load it into the editor
  // and refetch the list so the store (and the use case list page) match.
  const applyRolledBack = (restored: UseCaseConfig) => {
    setUc({
      ...restored,
      fields: restored.fields.map((f) => ({ ...f, options: [...f.options] })),
      checklist_items: restored.checklist_items.map((c) => ({
        ...c,
        options: [...(c.options ?? DEFAULT_CHECKLIST_OPTIONS)],
      })),
      additional_view_fields: (restored.additional_view_fields ?? []).map((d) => ({
        ...d,
        source_columns: [...d.source_columns],
      })),
    });
    setShowSaveError(false);
    retryFetch();
  };

  // Clear any stale save state left over from a prior session.
  useEffect(() => {
    resetSave();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect on successful save.
  useEffect(() => {
    if (saveSuccess) {
      resetSave();
      navigate("/vlmAudit/config");
    }
  }, [saveSuccess, navigate, resetSave]);

  // Show error banner on save failure; reset slice so user can retry.
  useEffect(() => {
    if (saveError) {
      setShowSaveError(true);
      resetSave();
    }
  }, [saveError, resetSave]);

  // ----------------------------  Validation  ----------------------------

  const nameError = useMemo(() => {
    const name = uc.name?.trim();
    if (!name) return "Name is required.";
    if (name.length > 100) return "Name must be 100 characters or fewer.";
    const clash = configs.some(
      (u) => u.id !== uc.id && u.name.trim().toLowerCase() === name.toLowerCase(),
    );
    if (clash) return "A use case with this name already exists.";
    return null;
  }, [uc.name, uc.id, configs]);

  const { fieldErrors, checklistErrors } = useMemo(
    () => validateConfig(uc.fields, uc.checklist_items),
    [uc.fields, uc.checklist_items],
  );

  const detailErrors = useMemo(
    () => validateDetails(uc.additional_view_fields ?? [], uc.additional_view_enable_flags ?? []),
    [uc.additional_view_fields, uc.additional_view_enable_flags],
  );

  // ----------------------------  Sampling helpers  ----------------------------

  const sampling = uc.review_sampling;
  const clampPct = (v: string | number) => Math.max(0, Math.min(100, Number(v) || 0));

  const setSampling = (patch: Partial<ReviewSampling>) => {
    const next: ReviewSampling = { ...sampling, ...patch };
    // Disabling 2nd also disables 3rd.
    if (patch.second_enabled === false) {
      next.third_enabled = false;
    }
    const effSecond = next.second_enabled ? next.second_pct : 0;
    const effThird = next.second_enabled && next.third_enabled ? next.third_pct : 0;
    const maxR = effThird > 0 ? 3 : effSecond > 0 ? 2 : 1;
    setUc((prev) => ({ ...prev, review_sampling: next, inter_rater_count: maxR }));
  };

  // Auditors tab: stored shares (only > 0%) must total 100 when anyone is assigned.
  const allocationTotal = (uc.allocations || []).reduce((n, a) => n + a.percent, 0);
  const allocationError =
    (uc.allocations || []).length > 0 && allocationTotal !== 100
      ? "Auditor allocations must total 100% (currently " + allocationTotal + "%)."
      : null;

  const samplingError =
    sampling.second_enabled && sampling.second_pct <= 0
      ? "Enter a 2nd-review % greater than 0, or disable the 2nd review."
      : sampling.second_enabled && sampling.third_enabled && sampling.third_pct <= 0
        ? "Enter a 3rd-review % greater than 0, or disable the 3rd review."
        : sampling.second_enabled &&
            sampling.third_enabled &&
            sampling.third_pct > sampling.second_pct
          ? "The 3rd-review % cannot exceed the 2nd-review %."
          : null;

  const allErrors = [
    ...(nameError ? [nameError] : []),
    ...fieldErrors,
    ...checklistErrors,
    ...detailErrors,
    ...(samplingError ? [samplingError] : []),
    ...(allocationError ? [allocationError] : []),
  ];
  const canSave = allErrors.length === 0;

  const persist = (forceActivate?: boolean) => {
    const ts = new Date().toISOString();
    const toSave: UseCaseConfig = {
      ...uc,
      // extra reviewers are now picked at random; drop any old fixed reviewer
      review_sampling: { ...uc.review_sampling, second_reviewer: "", third_reviewer: "" },
      status: forceActivate ? "active" : uc.status,
      updated_at: ts,
    };
    if (editing) {
      updateUseCase(toSave);
    } else {
      saveUseCase(toSave);
    }
  };

  // ----------------------------  Tab labels with counts  ----------------------------

  const tabs = TABS;

  const tabLabels: Record<TabKey, string> = {
    overview: "Overview",
    fields: "Fields (" + uc.fields.length + ")",
    checklist: "Checklist (" + uc.checklist_items.length + ")",
    details: "Additional View (" + (uc.additional_view_fields ?? []).length + ")",
    ingestion: "Ingestion Sampling",
    sampling: "Review Sampling",
    auditors: "Auditors",
    versions: "Versions",
  };

  // ----------------------------  Loading / Not Found  ----------------------------

  // A background refetch (e.g. after a rollback) keeps the editor mounted; the
  // loader only covers the first load or a retry that has nothing to show yet.
  if (editing && (!initialised || (isLoading && !configById(useCaseId!)))) {
    return (
      <Row width="fill" alignmentHorizontal="center">
        <Column height="60vh" alignmentVertical="center">
          <Loader />
        </Column>
      </Row>
    );
  }

  if (editing && initialised && isError) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          {authError
            ? "Unable to resolve your identity. Please refresh the page or try again later."
            : "Failed to load use case configurations."}
        </div>
        <div style={{ display: "flex", gap: "12px" }}>
          {fetchError && (
            <button style={S.btnPrimary} onClick={retryFetch}>
              Retry
            </button>
          )}
          <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/config")}>
            Back to list
          </button>
        </div>
      </div>
    );
  }

  if (editing && initialised && !configById(useCaseId!)) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          Use case not found. It may have been deleted.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/config")}>
          Back to list
        </button>
      </div>
    );
  }

  // A Program Owner may configure only the use cases an Admin granted them.
  if (editing && !canManageUseCase(useCaseId!)) {
    return (
      <div style={S.pageWrapper}>
        <div
          style={{
            ...S.devBanner,
            background: "#fee2e2",
            borderColor: "#ef4444",
            color: "#991b1b",
          }}
        >
          You do not have access to configure this use case. Ask an Admin to grant it to you.
        </div>
        <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/config")}>
          Back to list
        </button>
      </div>
    );
  }

  // ----------------------------  Render  ----------------------------

  return (
    <div style={S.pageWrapper}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div>
          <h1 style={S.pageTitle}>{editing ? uc.name || "Edit Use Case" : "New Use Case"}</h1>
          <p style={S.pageSubtitle}>
            Configure settings, audit fields, and checklist — all in one place.
          </p>
        </div>
        <button
          style={{ ...S.btnSecondary, fontSize: "0.875rem", padding: "8px 16px" }}
          onClick={() => navigate("/vlmAudit/config")}
        >
          ← All use cases
        </button>
      </div>

      {/* Tabs */}
      <div style={S.tabRow}>
        <div style={S.tabGroup}>
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={tab === t.key ? S.tabActive : S.tabInactive}
            >
              {tabLabels[t.key]}
            </button>
          ))}
        </div>
      </div>

      {/* Overview Tab */}
      {tab === "overview" && (
        <div style={{ ...S.configCard, maxWidth: "52rem" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
            {/* Name */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Use Case Name<span style={S.formLabelRequired}>*</span>
              </label>
              <input
                style={S.formInput}
                value={uc.name}
                onChange={(e) => setU({ name: e.target.value })}
                placeholder="e.g. Safety Genie: Cage Inspection"
              />
              <p style={S.formHelp}>Unique, max 100 characters.</p>
            </div>
            {/* Category */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Category<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={uc.category}
                onChange={(e) => setU({ category: e.target.value as UseCaseCategoryValue })}
              >
                {Object.values(USE_CASE_CATEGORY).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            {/* Description — full width */}
            <div style={{ ...S.formRow, gridColumn: "1 / -1" }}>
              <label style={S.formLabel}>Description</label>
              <textarea
                style={S.formTextarea}
                value={uc.description}
                onChange={(e) => setU({ description: e.target.value })}
                placeholder="What does this audit evaluate?"
              />
            </div>
            {/* Data Source Type */}
            <div style={S.formRow}>
              <label style={S.formLabel}>
                Data Source Type<span style={S.formLabelRequired}>*</span>
              </label>
              <select
                style={S.formSelect}
                value={uc.data_source_type}
                onChange={(e) => setU({ data_source_type: e.target.value as DataSourceTypeValue })}
              >
                {Object.values(DATA_SOURCE_TYPE).map((v) => (
                  <option key={v} value={v}>
                    {DATA_SOURCE_LABELS[v as DataSourceTypeValue]}
                  </option>
                ))}
              </select>
              <p style={S.formHelp}>Determines the workspace media viewer.</p>
            </div>
            {/* Active toggle — full width */}
            <div style={{ ...S.formRow, gridColumn: "1 / -1", paddingTop: "4px" }}>
              <label style={{ fontSize: "0.875rem", color: "#374151", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={uc.status === "active"}
                  onChange={(e) => setU({ status: e.target.checked ? "active" : "inactive" })}
                />
                Active (available in the audit queue)
              </label>
            </div>
          </div>
          {nameError && <div style={{ ...S.formError, marginTop: "8px" }}>{nameError}</div>}
        </div>
      )}

      {/* Fields Tab — split: builder left, preview right */}
      {tab === "fields" && (
        <div style={splitLayout}>
          <div style={splitLeft}>
            <div style={sectionTitle}>Fields</div>
            <FieldBuilder
              fields={uc.fields}
              onChange={(fields) => setU({ fields })}
              sourceColumns={sourceColumns}
            />
            {fieldErrors.length > 0 && (
              <div style={{ ...S.formError, marginTop: "8px" }}>
                {fieldErrors.map((e) => (
                  <div key={e}>• {e}</div>
                ))}
              </div>
            )}
          </div>
          <div style={splitRight}>
            <div style={sectionTitle}>Live Preview</div>
            <div style={previewBox}>
              <FormPreview config={uc} showChecklist={false} />
            </div>
          </div>
        </div>
      )}

      {/* Checklist Tab — split: builder left, preview right */}
      {tab === "checklist" && (
        <div style={splitLayout}>
          <div style={splitLeft}>
            <div style={sectionTitle}>Checklist (optional)</div>
            <ChecklistBuilder
              items={uc.checklist_items}
              onChange={(checklist_items) => setU({ checklist_items })}
              enableFlags={uc.checklist_enable_flags ?? []}
              onEnableFlagsChange={(checklist_enable_flags) => setU({ checklist_enable_flags })}
              sourceColumns={sourceColumns}
            />
            {checklistErrors.length > 0 && (
              <div style={{ ...S.formError, marginTop: "8px" }}>
                {checklistErrors.map((e) => (
                  <div key={e}>• {e}</div>
                ))}
              </div>
            )}
          </div>
          <div style={splitRight}>
            <div style={sectionTitle}>Preview</div>
            <div style={previewBox}>
              {uc.checklist_items.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {uc.checklist_items.map((item) => (
                    <div
                      key={item.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        fontSize: "0.875rem",
                      }}
                    >
                      <span style={{ color: "#374151" }}>{item.label || "(untitled item)"}</span>
                      <span style={{ display: "flex", gap: "4px" }}>
                        {(item.options ?? DEFAULT_CHECKLIST_OPTIONS).map((r, k) => (
                          <span
                            key={k}
                            style={{
                              fontSize: "0.7rem",
                              padding: "2px 8px",
                              border: "1px solid #e5e7eb",
                              borderRadius: "4px",
                              color: "#9ca3af",
                            }}
                          >
                            {r}
                          </span>
                        ))}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontSize: "0.875rem", color: "#9ca3af" }}>Add items to preview.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Additional View tab: split: builder left, preview right */}
      {tab === "details" && (
        <div style={splitLayout}>
          <div style={splitLeft}>
            <div style={sectionTitle}>Additional View</div>
            <DetailBuilder
              details={uc.additional_view_fields ?? []}
              onChange={(additional_view_fields) => setU({ additional_view_fields })}
              enableFlags={uc.additional_view_enable_flags ?? []}
              onEnableFlagsChange={(additional_view_enable_flags) =>
                setU({ additional_view_enable_flags })
              }
              sourceColumns={sourceColumns}
            />
            {detailErrors.length > 0 && (
              <div style={{ ...S.formError, marginTop: "8px" }}>
                {detailErrors.map((e) => (
                  <div key={e}>• {e}</div>
                ))}
              </div>
            )}
          </div>
          <div style={splitRight}>
            <div style={sectionTitle}>Live Preview</div>
            <div style={previewBox}>
              <DetailPreview
                details={uc.additional_view_fields ?? []}
                enableFlags={uc.additional_view_enable_flags ?? []}
              />
            </div>
          </div>
        </div>
      )}

      {/* Ingestion Tab */}
      {tab === "ingestion" && (
        <div style={{ ...S.configCard, maxWidth: "42rem" }}>
          <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "16px" }}>
            Configure how audit samples for this use case are ingested into the review queue.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px" }}>
            {/* Ingestion Source Type */}
            <div style={S.formRow}>
              <label style={S.formLabel}>Ingestion Source Type</label>
              <select
                style={S.formSelect}
                value={uc.ingestion_source_type}
                onChange={(e) =>
                  setU({ ingestion_source_type: e.target.value as IngestionSourceTypeValue })
                }
              >
                {Object.values(INGESTION_SOURCE_TYPE).map((v) => (
                  <option key={v} value={v}>
                    {INGESTION_SOURCE_LABELS[v as IngestionSourceTypeValue]}
                  </option>
                ))}
              </select>
              <p style={S.formHelp}>
                Where a sampling run draws this use case&apos;s records from.
              </p>
            </div>
            {/* Ingestion Source */}
            <div style={S.formRow}>
              <label style={S.formLabel}>Ingestion Source</label>
              <input
                style={S.formInput}
                value={uc.ingestion_source}
                onChange={(e) => setU({ ingestion_source: e.target.value })}
                placeholder="e.g. SGInspectionSync-beta-na"
              />
              <p style={S.formHelp}>
                Concrete pointer to that source (table name, S3 URI, or batch id).
              </p>
            </div>
            {/* Daily sample rate (used by the daily sampling job only) */}
            <div style={S.formRow}>
              <label style={S.formLabel}>Daily Sample Rate</label>
              <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <input
                  type="number"
                  style={{ ...S.formInput, width: "80px" }}
                  value={ingestion.sample_percent ?? ""}
                  onChange={(e) =>
                    setIngestion({
                      sample_percent:
                        e.target.value === ""
                          ? undefined
                          : Math.max(0, Math.min(100, Math.round(Number(e.target.value) || 0))),
                    })
                  }
                  onBlur={() => {
                    if (ingestion.sample_percent !== undefined && ingestion.sample_percent < 1)
                      setIngestion({ sample_percent: 1 });
                  }}
                  placeholder="100"
                  min={1}
                  max={100}
                  step={1}
                />
                <span style={{ color: "#6b7280" }}>%</span>
              </div>
              <p style={S.formHelp}>
                Share of each day&apos;s records the daily sampling job draws (1-100, empty = 100).
                The run below uses its own percent.
              </p>
            </div>
            {/* Excluded sites (daily job, manual run and preview) */}
            <div style={S.formRow}>
              <label style={S.formLabel}>Excluded Sites</label>
              <ExcludedSitesInput
                value={ingestion.excluded_sites ?? []}
                onChange={(excluded_sites) => setIngestion({ excluded_sites })}
              />
              <p style={S.formHelp}>
                Site codes never imported into the audit samples (daily job and the run below).
                Press Enter or comma to add.
              </p>
            </div>
          </div>
          {/* Admins and Program Owners scoped to this use case may preview and run sampling. */}
          {canManageUseCase(uc.id) && <SamplingRunPanel useCaseId={uc.id} saved={editing} />}
        </div>
      )}

      {/* Review Sampling Tab */}
      {tab === "sampling" && (
        <div style={{ ...S.configCard, maxWidth: "42rem" }}>
          <div style={sectionTitle}>Review Sampling</div>
          <p style={{ fontSize: "0.75rem", color: "#9ca3af", marginBottom: "16px" }}>
            Most cases get a single review. Enable a tier to sample a percentage of cases for an
            independent 2nd (and 3rd) review to measure inter-rater reliability. Extra reviewers are
            picked at random from the other assigned auditors on the Auditors tab, so no case gets
            the same person twice.
          </p>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
            {/* 2nd review */}
            <div>
              <label style={samplingCheckLabel}>
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={!!sampling.second_enabled}
                  onChange={(e) => setSampling({ second_enabled: e.target.checked })}
                />
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
                  Enable 2nd review
                </span>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px" }}>
                <input
                  type="number"
                  style={{ ...S.formInput, width: "80px" }}
                  value={sampling.second_enabled ? sampling.second_pct : ""}
                  onChange={(e) => setSampling({ second_pct: clampPct(e.target.value) })}
                  placeholder="0"
                  disabled={!sampling.second_enabled}
                  min={0}
                  max={100}
                />
                <span style={{ color: "#6b7280" }}>%</span>
              </div>
              <p style={{ ...S.formHelp, marginTop: "4px" }}>% of cases needing a 2nd review.</p>
            </div>

            {/* 3rd review */}
            <div style={{ opacity: sampling.second_enabled ? 1 : 0.5 }}>
              <label
                style={{
                  ...samplingCheckLabel,
                  cursor: sampling.second_enabled ? "pointer" : "not-allowed",
                }}
              >
                <input
                  type="checkbox"
                  style={S.formCheckbox}
                  checked={!!sampling.third_enabled}
                  disabled={!sampling.second_enabled}
                  onChange={(e) => setSampling({ third_enabled: e.target.checked })}
                />
                <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#374151" }}>
                  Enable 3rd review
                </span>
              </label>
              <div style={{ display: "flex", alignItems: "center", gap: "4px", marginTop: "8px" }}>
                <input
                  type="number"
                  style={{ ...S.formInput, width: "80px" }}
                  value={sampling.third_enabled ? sampling.third_pct : ""}
                  onChange={(e) => setSampling({ third_pct: clampPct(e.target.value) })}
                  placeholder="0"
                  disabled={!sampling.second_enabled || !sampling.third_enabled}
                  min={0}
                  max={100}
                />
                <span style={{ color: "#6b7280" }}>%</span>
              </div>
              <p style={{ ...S.formHelp, marginTop: "4px" }}>Must be &le; the 2nd-review %.</p>
            </div>
          </div>

          {samplingError && <div style={{ ...S.formError, marginTop: "8px" }}>{samplingError}</div>}

          {sampling.second_enabled ? (
            <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "12px" }}>
              Result: ~{100 - (sampling.second_pct || 0)}% single-review, ~
              {(sampling.second_pct || 0) - (sampling.third_enabled ? sampling.third_pct || 0 : 0)}%
              double-review, ~{sampling.third_enabled ? sampling.third_pct || 0 : 0}% triple-review.
            </div>
          ) : (
            <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "12px" }}>
              All cases get a single review (sampling disabled).
            </div>
          )}
        </div>
      )}

      {/* Auditors Tab */}
      {tab === "auditors" && (
        <AuditorsTab
          useCaseId={uc.id}
          useCaseName={uc.name}
          allocations={uc.allocations || []}
          onChange={(allocations) => setU({ allocations })}
        />
      )}

      {/* Versions Tab */}
      {tab === "versions" && (
        <VersionsTab useCaseId={uc.id} saved={editing} onRolledBack={applyRolledBack} />
      )}

      {/* Save error banner */}
      {showSaveError && (
        <div style={saveErrorBanner}>
          <span>Failed to save. Please check your connection and try again.</span>
          <button
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "#991b1b",
              fontWeight: 600,
              fontSize: "0.875rem",
            }}
            onClick={() => setShowSaveError(false)}
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Footer — Save actions (hidden on self-managed tabs) */}
      {tab !== "versions" && (
        <div style={S.wizardNav}>
          <button style={S.btnSecondary} onClick={() => navigate("/vlmAudit/config")}>
            Cancel
          </button>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            {allErrors.length > 0 && (
              <span style={{ fontSize: "0.75rem", color: "#ef4444" }}>
                {allErrors.length} issue(s) to resolve
              </span>
            )}
            <button
              style={{ ...S.btnOutline, opacity: canSave ? 1 : 0.5 }}
              disabled={!canSave || isSaving}
              onClick={() => persist()}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              style={{ ...S.btnPrimary, opacity: canSave ? 1 : 0.5 }}
              disabled={!canSave || isSaving}
              onClick={() => persist(true)}
            >
              {isSaving ? "Saving..." : "Save & Activate"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ----------------------------  AuditorsTab  ----------------------------

interface AuditorsTabProps {
  useCaseId: string;
  useCaseName: string;
  allocations: UseCaseAllocation[];
  onChange: (allocations: UseCaseAllocation[]) => void;
}

const isAdminMember = (m: PermissionsMember): boolean => !!m.elevated || m.role === ROLE.ADMIN;

const AuditorsTab: React.FC<AuditorsTabProps> = ({
  useCaseId,
  useCaseName,
  allocations,
  onChange,
}) => {
  const [search, setSearch] = React.useState("");
  const [group, setGroup] = React.useState<"auditors" | "admin">("auditors");
  const [page, setPage] = React.useState(0);
  // Aliases assigned when the tab opened; listed first (stable while editing).
  const [assigned] = React.useState<Set<string>>(() => new Set(allocations.map((a) => a.login)));
  // Checked rows (a checked row at 0% is not stored).
  const [selected, setSelected] = React.useState<Set<string>>(
    () => new Set(allocations.map((a) => a.login)),
  );
  const [pct, setPct] = React.useState<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    allocations.forEach((a) => {
      out[a.login] = a.percent;
    });
    return out;
  });
  const [auditors, setAuditors] = React.useState<PermissionsMember[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(false);

  // Push the stored shape up: checked rows with a share > 0 only.
  const emit = (nextSelected: Set<string>, nextPct: Record<string, number>) => {
    setSelected(nextSelected);
    setPct(nextPct);
    onChange(
      Array.from(nextSelected)
        .filter((l) => (nextPct[l] || 0) > 0)
        .map((l) => ({ login: l, percent: nextPct[l] })),
    );
  };

  React.useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    auditModuleService
      .getUseCaseMembers(useCaseId)
      .then((pool) => {
        if (!active) return;
        // Server-filtered: every Admin plus the members scoped to this use case.
        setAuditors(pool);
        // Drop stored shares for aliases no longer authorized on this use case.
        const inPool = new Set(pool.map((m) => m.login));
        if (allocations.some((a) => !inPool.has(a.login))) {
          emit(new Set(Array.from(selected).filter((l) => inPool.has(l))), pct);
        }
      })
      .catch(() => active && setLoadError(true))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [useCaseId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (login: string) => {
    const next = new Set(selected);
    const nextPct = { ...pct };
    if (next.has(login)) {
      next.delete(login);
      delete nextPct[login];
    } else {
      next.add(login);
    }
    emit(next, nextPct);
  };

  const total = Array.from(selected).reduce((n, l) => n + (pct[l] || 0), 0);
  // Search by alias or name; admins are listed in their own section.
  const query = search.trim().toLowerCase();
  const visible = auditors
    .filter(
      (a) =>
        !query || a.login.toLowerCase().includes(query) || a.name.toLowerCase().includes(query),
    )
    .sort((x, y) => Number(assigned.has(y.login)) - Number(assigned.has(x.login)));
  const adminRows = visible.filter(isAdminMember);
  const auditorRows = visible.filter((a) => !isAdminMember(a));

  const renderRow = (a: PermissionsMember) => {
    const checked = selected.has(a.login);
    return (
      <div key={a.login} style={auditorRow}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            cursor: "pointer",
            userSelect: "none" as const,
          }}
        >
          <input
            type="checkbox"
            checked={checked}
            onChange={() => toggle(a.login)}
            style={{ width: "18px", height: "18px", accentColor: "#0f7b8a" }}
          />
          <span style={{ fontSize: "0.875rem", fontWeight: 500, color: "#1f2937" }}>{a.name}</span>
          <span style={{ fontSize: "0.75rem", color: "#6b7280" }}>{a.login}</span>
        </label>
        <div style={{ display: "flex", alignItems: "center" }}>
          <input
            type="number"
            min={0}
            max={100}
            value={checked ? pct[a.login] ?? 0 : 0}
            onChange={(e) => emit(selected, { ...pct, [a.login]: Number(e.target.value) || 0 })}
            disabled={!checked}
            style={{
              ...S.formInput,
              width: "64px",
              textAlign: "right" as const,
              opacity: checked ? 1 : 0.4,
            }}
          />
          <span style={{ fontSize: "0.875rem", color: "#6b7280", marginLeft: "4px" }}>%</span>
        </div>
      </div>
    );
  };

  // Admins and auditors are on separate sub-tabs, paged AUDITORS_PAGE_SIZE at a time.
  const groupRows = group === "admin" ? adminRows : auditorRows;
  const pageCount = Math.max(1, Math.ceil(groupRows.length / AUDITORS_PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const pageRows = groupRows.slice(
    currentPage * AUDITORS_PAGE_SIZE,
    (currentPage + 1) * AUDITORS_PAGE_SIZE,
  );

  const switchGroup = (g: "auditors" | "admin") => {
    setGroup(g);
    setPage(0);
  };

  return (
    <div style={{ maxWidth: "42rem" }}>
      {/* Admin / Auditors sub-tabs */}
      <div style={{ ...S.tabGroup, marginBottom: "16px" }}>
        <button
          style={group === "auditors" ? S.tabActive : S.tabInactive}
          onClick={() => switchGroup("auditors")}
        >
          Auditors ({auditorRows.length})
        </button>
        <button
          style={group === "admin" ? S.tabActive : S.tabInactive}
          onClick={() => switchGroup("admin")}
        >
          Admin ({adminRows.length})
        </button>
      </div>

      <input
        type="text"
        placeholder="Search by alias or name"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setPage(0);
        }}
        style={{ ...S.formInput, width: "100%", marginBottom: "12px" }}
      />

      {/* Total (kept at the top so it stays visible with long member lists) */}
      <div style={totalBar}>
        <span style={{ color: "#4b5563" }}>
          Cases should be distributed 100% among the selected users.
        </span>
        <span
          style={{
            fontWeight: 700,
            fontSize: "1.125rem",
            color: total === 100 ? "#111827" : "#ef4444",
          }}
        >
          {total}%
        </span>
      </div>

      {/* Member rows */}
      {loading && (
        <div style={{ fontSize: "0.875rem", color: "#6b7280", padding: "12px 0" }}>
          Loading auditors…
        </div>
      )}
      {loadError && (
        <div style={{ fontSize: "0.875rem", color: "#ef4444", padding: "12px 0" }}>
          Could not load auditors. Please try again.
        </div>
      )}
      {!loading && !loadError && auditors.length === 0 && (
        <div style={{ fontSize: "0.875rem", color: "#6b7280", padding: "12px 0" }}>
          No auditors are authorized on this use case yet. Authorize members on{" "}
          <strong>{useCaseName || "this use case"}</strong> in Permissions &amp; Access first.
        </div>
      )}
      {!loading && !loadError && auditors.length > 0 && groupRows.length === 0 && (
        <div style={{ fontSize: "0.875rem", color: "#6b7280", padding: "12px 0" }}>
          {query ? 'No members match "' + search + '".' : "No members in this group."}
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        {pageRows.map(renderRow)}
      </div>

      {/* Pager */}
      {pageCount > 1 && (
        <div style={pagerRow}>
          <button
            style={{ ...S.btnSecondary, opacity: currentPage > 0 ? 1 : 0.5 }}
            disabled={currentPage === 0}
            onClick={() => setPage(currentPage - 1)}
          >
            {"\u2039"} Prev
          </button>
          <span style={{ fontSize: "0.875rem", color: "#6b7280" }}>
            Page {currentPage + 1} of {pageCount} ({groupRows.length} members)
          </span>
          <button
            style={{ ...S.btnSecondary, opacity: currentPage < pageCount - 1 ? 1 : 0.5 }}
            disabled={currentPage >= pageCount - 1}
            onClick={() => setPage(currentPage + 1)}
          >
            Next {"\u203A"}
          </button>
        </div>
      )}
    </div>
  );
};

// ----------------------------  SamplingRunPanel  ----------------------------

// Local-calendar YYYY-MM-DD, the value format of <input type="date">.
const toDateInput = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return d.getFullYear() + "-" + pad(d.getMonth() + 1) + "-" + pad(d.getDate());
};

const errorDetail = (err: unknown): string => {
  const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
  const detail = e?.response?.data?.detail;
  return typeof detail === "string" ? detail : e?.message || "Request failed";
};

// Most new inspections one Submit may import. UI-only: the API has no cap, but a larger
// run would outlast the UI's 29s request timeout.
export const MAX_NEW_PER_RUN = 500;

// Sends the inspections completed in [startDate, endDate] to the review queue. Submit is
// only enabled once a preview for the same range has shown what will be written.
// saved=false (a new, unsaved use case) disables the panel: the sampling API needs
// the use case to exist.
export const SamplingRunPanel: React.FC<{ useCaseId: string; saved?: boolean }> = ({
  useCaseId,
  saved = true,
}) => {
  const [startDate, setStartDate] = React.useState(() =>
    toDateInput(new Date(Date.now() - 6 * 86400000)),
  );
  const [endDate, setEndDate] = React.useState(() => toDateInput(new Date()));
  // Kept as the raw input text so the field can be cleared while typing.
  const [sampleRate, setSampleRate] = React.useState("100");
  const [preview, setPreview] = React.useState<SamplingRunResult | null>(null);
  const [submitResult, setSubmitResult] = React.useState<SamplingRunResult | null>(null);
  const [busy, setBusy] = React.useState<"preview" | "submit" | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const config: SamplingRunConfig = {
    useCaseId,
    startDate,
    endDate,
    samplePercent: Number(sampleRate),
    // Fixed so a resubmit of the same range draws the same records.
    seed: 1,
  };

  // ISO dates compare correctly as strings.
  const rangeError =
    !startDate || !endDate
      ? "Select a start and end date."
      : startDate > endDate
        ? "Start date must be on or before the end date."
        : null;
  const rate = Number(sampleRate);
  const rateError =
    sampleRate.trim() === "" || !Number.isInteger(rate) || rate < 1 || rate > 100
      ? "Sample rate must be a whole number from 1 to 100."
      : null;

  const resetResults = () => {
    setPreview(null);
    setSubmitResult(null);
    setError(null);
  };

  const call = async (kind: "preview" | "submit") => {
    setBusy(kind);
    setError(null);
    try {
      if (kind === "preview") {
        setPreview(await auditModuleService.previewSampling(config));
        setSubmitResult(null);
      } else {
        setSubmitResult(await auditModuleService.submitSampling(config));
      }
    } catch (err) {
      setError(errorDetail(err));
    } finally {
      setBusy(null);
    }
  };

  const canPreview = saved && busy === null && !rangeError && !rateError;
  // ingested on a preview is the number of new records a submit would import.
  const overLimit = !!preview && (preview.ingested ?? 0) > MAX_NEW_PER_RUN;
  const limitError = overLimit
    ? "Please reduce the date range or sample rate. The ingestion limit for a run is " +
      MAX_NEW_PER_RUN +
      " new inspections; this run would import " +
      preview!.ingested +
      "."
    : null;
  const canSubmit =
    saved && !!preview && preview.sampleSize > 0 && !overLimit && !submitResult && busy === null;

  return (
    <div style={{ borderTop: "1px solid #e5e7eb", marginTop: "20px", paddingTop: "16px" }}>
      <div style={sectionTitle}>Assign Audit Tasks</div>
      <p style={S.formHelp}>
        Assign new or no auditor inspections in this date range to auditors. Preview first before
        assigning.
      </p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          gap: "16px 24px",
          marginTop: "12px",
        }}
      >
        <div style={S.formRow}>
          <label style={S.formLabel} htmlFor="sampling-start-date">
            Start Date
          </label>
          <input
            id="sampling-start-date"
            type="date"
            style={S.formInput}
            value={startDate}
            max={endDate || undefined}
            onChange={(e) => {
              setStartDate(e.target.value);
              resetResults();
            }}
          />
        </div>
        <div style={S.formRow}>
          <label style={S.formLabel} htmlFor="sampling-end-date">
            End Date
          </label>
          <input
            id="sampling-end-date"
            type="date"
            style={S.formInput}
            value={endDate}
            min={startDate || undefined}
            onChange={(e) => {
              setEndDate(e.target.value);
              resetResults();
            }}
          />
        </div>
        <div style={S.formRow}>
          <label style={S.formLabel} htmlFor="sampling-rate">
            Sample Rate (%)
          </label>
          <input
            id="sampling-rate"
            type="number"
            min={1}
            max={100}
            step={1}
            style={S.formInput}
            value={sampleRate}
            onChange={(e) => {
              setSampleRate(e.target.value);
              resetResults();
            }}
          />
        </div>
      </div>
      <p style={S.formHelp}>
        Both dates are inclusive. Sample rate is the % of each site's records in the range to
        import; 100 imports all of them.
      </p>

      <div style={{ display: "flex", gap: "12px", marginTop: "16px" }}>
        <button
          style={{ ...S.btnOutline, opacity: canPreview ? 1 : 0.5 }}
          disabled={!canPreview}
          onClick={() => call("preview")}
        >
          {busy === "preview" ? "Previewing..." : "Preview"}
        </button>
        <button
          style={{ ...S.btnPrimary, cursor: canSubmit ? "pointer" : "not-allowed" }}
          disabled={!canSubmit}
          onClick={() => call("submit")}
        >
          {busy === "submit" ? "Submitting..." : "Submit"}
        </button>
      </div>

      {!saved && (
        <p style={{ ...S.formHelp, marginTop: "8px" }}>
          Save the use case before assigning audit tasks.
        </p>
      )}
      {rangeError && <div style={{ ...S.formError, marginTop: "8px" }}>{rangeError}</div>}
      {rateError && <div style={{ ...S.formError, marginTop: "8px" }}>{rateError}</div>}
      {limitError && <div style={{ ...S.formError, marginTop: "8px" }}>{limitError}</div>}
      {error && <div style={{ ...S.formError, marginTop: "8px" }}>{error}</div>}
      {preview && (
        <div style={{ fontSize: "0.875rem", color: "#374151", marginTop: "12px", lineHeight: 1.6 }}>
          <div>
            Population <strong>{preview.population}</strong>, sample size{" "}
            <strong>{preview.sampleSize}</strong>
            {preview.sampleSize === 0 && " (nothing to submit for this range)"}
          </div>
          {/* Older backends return null counts on preview; omit them rather than show 0s. */}
          {preview.sampleSize > 0 && preview.ingested != null && (
            <>
              <div>
                Will import new: <strong>{preview.ingested}</strong>
              </div>
              <div>
                Will update (no auditors): <strong>{preview.auditorsUpdated ?? 0}</strong>
              </div>
              <div>
                Will skip (already sampled with auditor): <strong>{preview.skipped ?? 0}</strong>
              </div>
            </>
          )}
          {Object.keys(preview.allocation || {}).length > 0 && (
            <div>
              Per site:{" "}
              {Object.keys(preview.allocation)
                .sort()
                .map((site) => (site || "(no site)") + ": " + preview.allocation[site])
                .join(", ")}
            </div>
          )}
        </div>
      )}
      {submitResult && (
        <p style={{ fontSize: "0.875rem", color: "#374151", marginTop: "4px" }}>
          Imported new <strong>{submitResult.ingested ?? 0}</strong>, updated (no auditors){" "}
          <strong>{submitResult.auditorsUpdated ?? 0}</strong>, skipped (already sampled with
          auditor) <strong>{submitResult.skipped ?? 0}</strong>, failed{" "}
          <strong style={{ color: submitResult.failed ? "#ef4444" : undefined }}>
            {submitResult.failed ?? 0}
          </strong>
          {submitResult.batchId && <> — batch {submitResult.batchId}</>}.
        </p>
      )}
    </div>
  );
};

// ----------------------------  Layout Styles  ----------------------------

const splitLayout: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "24px",
  alignItems: "start",
};

const splitLeft: React.CSSProperties = {
  minWidth: 0,
};

const splitRight: React.CSSProperties = {
  minWidth: 0,
  position: "sticky",
  top: "80px",
};

const sectionTitle: React.CSSProperties = {
  fontSize: "0.875rem",
  fontWeight: 600,
  color: "#374151",
  marginBottom: "8px",
};

const previewBox: React.CSSProperties = {
  background: "#f9fafb",
  borderRadius: "12px",
  border: "1px solid #e5e7eb",
  padding: "16px",
};

const saveErrorBanner: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  background: "#fee2e2",
  border: "1px solid #fca5a5",
  borderRadius: "8px",
  padding: "12px 16px",
  marginTop: "16px",
  maxWidth: "52rem",
  fontSize: "0.875rem",
  color: "#991b1b",
};

const samplingCheckLabel: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  userSelect: "none",
  marginBottom: "4px",
};

const AUDITORS_PAGE_SIZE = 10;

const totalBar: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  fontSize: "0.875rem",
  padding: "12px 16px",
  marginBottom: "16px",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  background: "#f9fafb",
};

const pagerRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  marginTop: "16px",
};

const auditorRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  border: "1px solid #e5e7eb",
  borderRadius: "8px",
  padding: "10px 16px",
  background: "white",
};

export default UseCaseEditor;
